import {
  AlertTriangle,
  Boxes,
  Download,
  Edit2,
  Eye,
  EyeOff,
  History,
  MoreVertical,
  Package,
  Plus,
  Search,
  Tag,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '../../../types/models';
import type { ProductCreate } from '../../../utils/productsAPI';
import { CategoryManager } from '@/features/catalog/components/CategoryManager';
import ProductForm from '@/features/catalog/components/ProductForm';
import { Badge, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui';
import { Button } from '@/components/ui-v2';
import { confirm } from '../../../stores/confirmStore';
import { useExportProducts } from '../hooks/useExportProducts';
import { CSVImport } from '@/components/CSVImport';
import { StockAdjustModal } from './StockAdjustModal';
import { StockMovementHistory } from './StockMovementHistory';
import {
  useProducts,
  useProductCategories,
  useTaxRates,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from '../hooks/useProducts';

type ViewMode = 'products' | 'categories';

const ProductsManagementResponsive: React.FC = () => {
  const { t } = useTranslation();
  // UI State
  const [viewMode, setViewMode] = useState<ViewMode>('products');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [stockAdjustProduct, setStockAdjustProduct] = useState<Product | null>(null);
  const [stockHistoryProduct, setStockHistoryProduct] = useState<Product | null>(null);

  // Build filters object for query (memoized to prevent unnecessary refetches)
  const filters = useMemo(() => ({
    categoryId: categoryFilter || undefined,
    active: activeFilter,
    search: searchTerm || undefined,
  }), [categoryFilter, activeFilter, searchTerm]);

  // TanStack Query hooks
  const { data: products = [], isLoading: loading, refetch: refetchProducts } = useProducts(filters);
  const { data: categories = [] } = useProductCategories();
  const { data: taxRates = [] } = useTaxRates();

  // Mutations
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSearch = () => {
    refetchProducts();
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async (product: Product) => {
    const confirmed = await confirm({
      title: t('products.delete'),
      message: t('products.deleteConfirm', { name: product.name }),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger'
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteProduct.mutateAsync(product.id);
      showMessage('success', t('products.deleteSuccess'));
    } catch (error: any) {
      showMessage('error', error.message || t('products.deleteError'));
    }
  };

  const handleFormSubmit = async (productData: ProductCreate) => {
    try {
      if (editingProduct) {
        await updateProduct.mutateAsync({ id: editingProduct.id, data: productData });
        showMessage('success', t('products.updateSuccess'));
      } else {
        await createProduct.mutateAsync(productData);
        showMessage('success', t('products.createSuccess'));
      }
      setShowForm(false);
      setEditingProduct(null);
    } catch (error: any) {
      showMessage('error', error.message || t('products.saveError'));
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleStockAdjust = (product: Product) => {
    setStockAdjustProduct(product);
    setActiveMenu(null);
  };

  const handleStockAdjustSuccess = () => {
    showMessage('success', t('products.stockAdjustSuccess'));
    refetchProducts();
  };

  const handleViewStockHistory = (product: Product) => {
    setStockHistoryProduct(product);
    setActiveMenu(null);
  };

  // Export CSV (architecture 3 couches : Composant → Hook → Service)
  const { exportCSV, isExporting } = useExportProducts();

  const handleExportCSV = () => {
    exportCSV(products);
  };

  const stats = {
    total: products.length,
    active: products.filter((p) => p.isActive).length,  // ✅ CORRIGÉ: V2
    withStock: products.filter((p) => p.trackInventory && p.stockQuantity !== null && p.stockQuantity !== undefined).length,  // ✅ CORRIGÉ: V2
    lowStock: products.filter(
      (p) =>
        p.trackInventory &&
        p.stockQuantity !== null &&
        p.stockQuantity !== undefined &&
        p.lowStockAlert !== null &&
        p.lowStockAlert !== undefined &&
        p.stockQuantity <= p.lowStockAlert  // ✅ CORRIGÉ: V2
    ).length,
  };

  if (showForm) {
    return (
      <div>
        <Button variant="ghost" onClick={handleFormCancel} className="mb-4">
          {t('common.actions.back')}
        </Button>
        <ProductForm
          product={editingProduct}
          tvaRates={taxRates}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  if (viewMode === 'categories') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
          <button
            onClick={() => setViewMode('products')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Package className="w-5 h-5" />
            <span className="font-medium">{t('products.tabs.products')}</span>
          </button>
          <button
            onClick={() => setViewMode('categories')}
            className="flex items-center gap-2 px-4 py-2 text-teal-600 border-b-2 border-teal-600"
          >
            <Tag className="w-5 h-5" />
            <span className="font-medium">{t('products.tabs.categories')}</span>
          </button>
        </div>
        <CategoryManager />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/20 pb-2 overflow-x-auto">
        <button
          onClick={() => setViewMode('products')}
          className="flex items-center gap-2 px-4 py-2 text-teal-600 border-b-2 border-teal-600 whitespace-nowrap"
        >
          <Package className="w-5 h-5" />
          <span className="font-medium">{t('products.tabs.products')}</span>
        </button>
        <button
          onClick={() => setViewMode('categories')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
        >
          <Tag className="w-5 h-5" />
          <span className="font-medium">{t('products.tabs.categories')}</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">{t('products.titleProducts')}</h1>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">{t('products.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {products.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={isExporting}
              icon={<Download className="w-4 h-4" />}
            >
              {isExporting ? t('common.export.exporting') : t('common.exportCSV')}
            </Button>
          )}
          <CSVImport entityType="produits" onImportComplete={refetchProducts} />
          <Button onClick={handleCreateProduct} variant="secondary" icon={<Plus className="w-5 h-5" />}>
            {t('products.newProduct')}
          </Button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-xl ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-4 hover:bg-white/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">{t('products.stats.total')}</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Package className="w-8 h-8 text-teal-500" />
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-4 hover:bg-white/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">{t('products.stats.active')}</p>
              <p className="text-xl font-bold text-green-600">{stats.active}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-4 hover:bg-white/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">{t('products.stats.withStock')}</p>
              <p className="text-xl font-bold text-teal-600">{stats.withStock}</p>
            </div>
            <Package className="w-8 h-8 text-teal-500" />
          </div>
        </div>

        {stats.lowStock > 0 && (
          <div className="bg-orange-50/30 backdrop-blur-xl rounded-2xl shadow-lg border border-orange-200/40 p-4 hover:bg-orange-50/40 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600">{t('products.stats.lowStock')}</p>
                <p className="text-xl font-bold text-orange-600">{stats.lowStock}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="sm:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('products.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
              />
            </div>
          </div>

          {/* Category filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
            >
              <option value="">{t('products.filters.allCategories')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div>
            <select
              value={activeFilter === undefined ? '' : activeFilter ? 'true' : 'false'}
              onChange={(e) =>
                setActiveFilter(e.target.value === '' ? undefined : e.target.value === 'true')
              }
              className="w-full px-4 py-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
            >
              <option value="">{t('products.filters.allStatuses')}</option>
              <option value="true">{t('products.filters.activeOnly')}</option>
              <option value="false">{t('products.filters.inactiveOnly')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && products.length === 0 && (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('products.noProductsFound')}</h3>
          <p className="text-gray-600 mb-6">{t('products.createFirstDescription')}</p>
          <div className="flex justify-center">
            <Button onClick={handleCreateProduct} variant="secondary" icon={<Plus className="w-5 h-5" />}>
              {t('products.createFirst')}
            </Button>
          </div>
        </div>
      )}

      {/* Products Grid - Mobile Cards */}
      {!loading && products.length > 0 && (
        <>
          {/* Mobile View */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {products.map((product) => {
              const unitPrice = Number(product.unitPrice) || 0;  // ✅ CORRIGÉ: V2 + Conversion Decimal
              const taxRate = taxRates.find(rate => rate.id === product.taxRateId);
              const tvaRate = taxRate ? Number(taxRate.rate) : 0;  // ✅ CORRIGÉ: V2 + Conversion Decimal
              const priceTTC = unitPrice * (1 + tvaRate / 100);
              const isLowStock =
                product.trackInventory &&
                product.stockQuantity !== null &&
                product.stockQuantity !== undefined &&
                product.lowStockAlert !== null &&
                product.lowStockAlert !== undefined &&
                product.stockQuantity <= product.lowStockAlert;  // ✅ CORRIGÉ: V2

              return (
                <Card key={product.id} padding="none" className="overflow-hidden bg-white/20 backdrop-blur-xl border border-white/30 hover:bg-white/35 hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="bg-white/10 backdrop-blur-sm p-4 border-b border-white/20">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-bold text-gray-900 mb-1 truncate">
                          {product.name}
                        </CardTitle>
                        {product.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                        )}
                      </div>
                      <div className="relative ml-2">
                        <button
                          onClick={() => setActiveMenu(activeMenu === product.id ? null : product.id)}
                          className="p-2 hover:bg-white/30 hover:backdrop-blur-sm rounded-lg transition-all duration-300"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>

                        {activeMenu === product.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 py-1 z-10">
                            <button
                              onClick={() => {
                                handleEditProduct(product);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit2 className="w-4 h-4" />
                              {t('common.actions.edit')}
                            </button>
                            {product.trackInventory && (
                              <>
                                <button
                                  onClick={() => handleStockAdjust(product)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-teal-700 hover:bg-teal-50"
                                >
                                  <Boxes className="w-4 h-4" />
                                  {t('products.stock.adjustStock')}
                                </button>
                                <button
                                  onClick={() => handleViewStockHistory(product)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <History className="w-4 h-4" />
                                  {t('products.stock.historyStock')}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                handleDeleteProduct(product);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('common.actions.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-4 space-y-3">
                    {/* Category */}
                    {product.ProductCategory && (
                      <Badge variant="info" size="sm">
                        {product.ProductCategory.name}
                      </Badge>
                    )}

                    {/* Prices */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{t('products.fields.priceHT')}</span>
                        <span className="font-semibold text-gray-900">
                          {unitPrice.toFixed(2)} € / {product.unit}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('products.fields.priceTTC')} (TVA {tvaRate}%)</span>
                        <span className="text-lg font-bold text-gray-900">{priceTTC.toFixed(2)} €</span>
                      </div>
                    </div>

                    {/* Stock */}
                    {product.stockQuantity !== null && (
                      <div
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          isLowStock ? 'bg-orange-50' : 'bg-gray-50'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-700">{t('products.stock.stockLabel')}</span>
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-bold ${isLowStock ? 'text-orange-600' : 'text-gray-900'}`}
                          >
                            {product.stockQuantity}
                          </span>
                          {isLowStock && <AlertTriangle className="w-4 h-4 text-orange-600" />}
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{t('common.labels.status')}</span>
                      <Badge variant={product.isActive ? 'success' : 'pending'} size="sm">
                        {product.isActive ? (
                          <Eye className="w-3 h-3" />
                        ) : (
                          <EyeOff className="w-3 h-3" />
                        )}
                        {product.isActive ? t('common.labels.active') : t('common.labels.inactive')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-white/10 backdrop-blur-sm border-b border-white/20">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.product')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.reference')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.category')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.priceHT')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.taxRate')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('products.fields.priceTTC')}
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {t('products.stock.stockLabel')}
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {t('common.labels.status')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('common.labels.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {products.map((product) => {
                      const unitPrice = Number(product.unitPrice) || 0;
                      const taxRate = taxRates.find(rate => rate.id === product.taxRateId);
                      const tvaRate = taxRate ? Number(taxRate.rate) : 0;
                      const priceTTC = unitPrice * (1 + tvaRate / 100);
                      const isLowStock =
                        product.trackInventory &&
                        product.stockQuantity !== null &&
                        product.stockQuantity !== undefined &&
                        product.lowStockAlert !== null &&
                        product.lowStockAlert !== undefined &&
                        product.stockQuantity <= product.lowStockAlert;

                      return (
                        <tr key={product.id} className="hover:bg-white/10 transition-all duration-300">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              {product.description && (
                                <div className="text-sm text-gray-500 max-w-xs truncate">
                                  {product.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{product.sku || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {product.ProductCategory && <Badge variant="info">{product.ProductCategory.name}</Badge>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {unitPrice.toFixed(2)} €
                            </div>
                            <div className="text-xs text-gray-500">/{product.unit}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm text-gray-900">{tvaRate}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {priceTTC.toFixed(2)} €
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {product.stockQuantity !== null ? (
                              <div
                                className={`text-sm font-medium ${
                                  isLowStock ? 'text-orange-600' : 'text-gray-900'
                                }`}
                              >
                                {product.stockQuantity}
                                {isLowStock && <AlertTriangle className="w-4 h-4 inline ml-1" />}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <Badge variant={product.isActive ? 'success' : 'pending'}>
                              {product.isActive ? t('common.labels.active') : t('common.labels.inactive')}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors mr-1"
                              title={t('common.actions.edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {product.trackInventory && (
                              <>
                                <button
                                  onClick={() => handleStockAdjust(product)}
                                  className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors mr-1"
                                  title={t('products.stock.adjustStock')}
                                >
                                  <Boxes className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleViewStockHistory(product)}
                                  className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-1"
                                  title={t('products.stock.historyStock')}
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteProduct(product)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          </div>
        </>
      )}

      {/* Stock Adjustment Modal */}
      {stockAdjustProduct && (
        <StockAdjustModal
          product={stockAdjustProduct}
          isOpen={!!stockAdjustProduct}
          onClose={() => setStockAdjustProduct(null)}
          onSuccess={handleStockAdjustSuccess}
        />
      )}

      {/* Stock Movement History Modal */}
      {stockHistoryProduct && (
        <StockMovementHistory
          product={stockHistoryProduct}
          isOpen={!!stockHistoryProduct}
          onClose={() => setStockHistoryProduct(null)}
        />
      )}
    </div>
  );
};

export default ProductsManagementResponsive;
